import { createCsvHeaderResolver } from './csv-header'

/**
 * 組織マスターCSVの列定義（唯一の情報源）。
 *
 * 人事システムからの出力（社員コード / 社員氏名 / 人事組織名称(略) / 社員メールアドレス /
 * 直属上司コード / 直属上司名 / 直属上司メールアドレス）をそのまま取り込めるようにしつつ、
 * 旧来の英語ヘッダーも後方互換として受け付ける。
 *
 * backend（パース）と frontend（案内表示・テンプレート生成）の双方がここを参照する。
 */

/** CSV列の正規キー */
export const ORGANIZATION_CSV_FIELDS = [
  'employeeNumber',
  'employeeName',
  'departmentName',
  'email',
  'positionName',
  'supervisorEmployeeNumber',
  'supervisorName',
  'supervisorEmail',
] as const

export type OrganizationCsvField = (typeof ORGANIZATION_CSV_FIELDS)[number]

/** 正規キー → 受け付けるヘッダー表記 */
export const ORGANIZATION_CSV_HEADER_ALIASES: Record<OrganizationCsvField, string[]> = {
  employeeNumber: ['社員コード', '社員番号', 'employee_number', 'employee_code'],
  employeeName: ['社員氏名', '社員名', '氏名', 'employee_name', 'user_name'],
  departmentName: [
    '人事組織名称(略)',
    '人事組織名称',
    '組織名称',
    '部署名',
    '部門名',
    'department_name',
  ],
  email: ['社員メールアドレス', 'メールアドレス', 'user_email', 'email'],
  positionName: ['役職名', '役職', 'position_name'],
  supervisorEmployeeNumber: [
    '直属上司コード',
    '上司コード',
    'parent_employee_number',
    'supervisor_code',
  ],
  supervisorName: ['直属上司名', '上司名', 'supervisor_name'],
  supervisorEmail: ['直属上司メールアドレス', '上司メールアドレス', 'supervisor_email'],
}

/** 画面案内・テンプレート出力に使う推奨ヘッダー（人事システムの出力そのまま） */
export const ORGANIZATION_CSV_TEMPLATE_HEADERS = [
  '社員コード',
  '社員氏名',
  '人事組織名称(略)',
  '社員メールアドレス',
  '直属上司コード',
  '直属上司名',
  '直属上司メールアドレス',
] as const

/** 必須列（欠けている行はエラーとして報告する） */
export const ORGANIZATION_CSV_REQUIRED_FIELDS: OrganizationCsvField[] = ['employeeNumber', 'email']

/** 表示用の日本語ラベル */
export const ORGANIZATION_CSV_FIELD_LABELS: Record<OrganizationCsvField, string> = {
  employeeNumber: '社員コード',
  employeeName: '社員氏名',
  departmentName: '人事組織名称(略)',
  email: '社員メールアドレス',
  positionName: '役職名',
  supervisorEmployeeNumber: '直属上司コード',
  supervisorName: '直属上司名',
  supervisorEmail: '直属上司メールアドレス',
}

/**
 * CSVヘッダーを正規キーに変換する。未知のヘッダーは正規化した文字列をそのまま返す。
 * Papa Parse の `transformHeader` にそのまま渡せる（冪等）。
 */
export const toOrganizationCsvField = createCsvHeaderResolver(
  ORGANIZATION_CSV_FIELDS,
  ORGANIZATION_CSV_HEADER_ALIASES,
)
